import { QuickTradeData } from '../reducer/orders.reducer'
import { Broker_OrderType, OrderType, Product, TransactionType, Validity } from '../proto/generated/order'
import { Security, SecurityCategory_CategoryCode } from '../proto/generated/security'
import { resolveQuickTradeLimitPrice, resolveQuickTradeProduct } from './openQuickTrade'

const STORAGE_KEY = 'quickTradePreferences'

export type QuickTradeMarketLimit = 'market' | 'limit'

export type QuickTradeSavedSettings = {
    mode: QuickTradeData['mode']
    marketLimit: QuickTradeMarketLimit
    product: Product
}

const VALID_MODES: QuickTradeData['mode'][] = ['regular', 'trigger', 'iceberg', 'slOrTarget']

const DEFAULT_SETTINGS: QuickTradeSavedSettings = {
    mode: 'regular',
    marketLimit: 'market',
    product: Product.INTRADAY,
}

export function isMarketBrokerOrderType(brokerOrderType: Broker_OrderType): boolean {
    return [Broker_OrderType.MARKET_ORDER, Broker_OrderType.STOPLOSS_MARKET_ORDER].includes(brokerOrderType)
}

export function marketLimitFromBrokerOrderType(brokerOrderType: Broker_OrderType): QuickTradeMarketLimit {
    return isMarketBrokerOrderType(brokerOrderType) ? 'market' : 'limit'
}

export function resolveOrderTypeForMode(mode: QuickTradeData['mode']): OrderType {
    switch (mode) {
        case 'trigger':
            return OrderType.TRIGGER
        case 'iceberg':
            return OrderType.ICEBERG
        default:
            return OrderType.NORMAL
    }
}

export function resolveBrokerOrderTypeForMode(
    mode: QuickTradeData['mode'],
    marketLimit: QuickTradeMarketLimit
): Broker_OrderType {
    if (mode === 'iceberg') {
        return Broker_OrderType.LIMIT_ORDER
    }
    if (mode === 'trigger') {
        return marketLimit === 'market'
            ? Broker_OrderType.STOPLOSS_MARKET_ORDER
            : Broker_OrderType.STOPLOSS_ORDER
    }
    return marketLimit === 'market' ? Broker_OrderType.MARKET_ORDER : Broker_OrderType.LIMIT_ORDER
}

export function readQuickTradePreferences(): QuickTradeSavedSettings {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return DEFAULT_SETTINGS

        const parsed = JSON.parse(raw) as Partial<QuickTradeSavedSettings>
        const mode = VALID_MODES.includes(parsed.mode as QuickTradeData['mode'])
            ? (parsed.mode as QuickTradeData['mode'])
            : DEFAULT_SETTINGS.mode
        const marketLimit = parsed.marketLimit === 'limit' ? 'limit' : 'market'
        const product = [Product.INTRADAY, Product.CNC, Product.MARGIN, Product.MTF].includes(parsed.product as Product)
            ? (parsed.product as Product)
            : DEFAULT_SETTINGS.product

        return { mode, marketLimit, product }
    } catch {
        return DEFAULT_SETTINGS
    }
}

export function saveQuickTradePreferences(updates: Partial<QuickTradeSavedSettings>): void {
    const current = readQuickTradePreferences()
    const next: QuickTradeSavedSettings = {
        ...current,
        ...updates,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

export function saveQuickTradePreferencesFromOrderState(
    mode: QuickTradeData['mode'],
    brokerOrderType: Broker_OrderType,
    product: Product
): void {
    saveQuickTradePreferences({
        mode,
        marketLimit: marketLimitFromBrokerOrderType(brokerOrderType),
        product,
    })
}

function resolveProductForSecurity(security: Security | undefined, savedProduct: Product): Product {
    if (!security) return savedProduct
    return resolveQuickTradeProduct(security, savedProduct)
}

function resolveOpenPrice(
    mode: QuickTradeData['mode'],
    brokerOrderType: Broker_OrderType,
    security: Security | undefined,
    transactionType: TransactionType | undefined,
    explicitPrice?: number,
    explicitTriggerPrice?: number
): { price?: number; triggerPrice: number } {
    const hasExplicitPrice = explicitPrice !== undefined && explicitPrice > 0

    if (mode === 'trigger') {
        const triggerPrice =
            explicitTriggerPrice && explicitTriggerPrice > 0
                ? explicitTriggerPrice
                : hasExplicitPrice
                  ? explicitPrice!
                  : security && transactionType
                    ? resolveQuickTradeLimitPrice(security, transactionType) ?? 0
                    : 0
        return { price: explicitPrice, triggerPrice }
    }

    if (isMarketBrokerOrderType(brokerOrderType)) {
        return { price: undefined, triggerPrice: 0 }
    }

    if (hasExplicitPrice) {
        return { price: explicitPrice, triggerPrice: 0 }
    }

    const price =
        security && transactionType ? resolveQuickTradeLimitPrice(security, transactionType) : undefined
    return { price, triggerPrice: 0 }
}

/** Apply saved mode / market-limit / product and reset ephemeral fields when opening quick trade. */
export function prepareQuickTradeOpenPayload(incoming: Partial<QuickTradeData>): Partial<QuickTradeData> {
    if (!incoming.openDialog) return incoming
    if (incoming.type === 'modify' || incoming.type === 'notTradable') return incoming
    if (incoming.isSquareOffOrder) return prepareExitQuickTradeOpenPayload(incoming)

    const saved = readQuickTradePreferences()
    const security = incoming.order?.security
    const transactionType = incoming.order?.transactionType
    const explicitPrice = incoming.order?.price
    const hasExplicitPrice = explicitPrice !== undefined && explicitPrice > 0

    let mode = saved.mode
    let brokerOrderType = resolveBrokerOrderTypeForMode(mode, saved.marketLimit)
    let orderType = resolveOrderTypeForMode(mode)

    // Clicking a depth row or chart price should still open limit at that price.
    if (hasExplicitPrice && mode === 'regular') {
        brokerOrderType = Broker_OrderType.LIMIT_ORDER
        orderType = OrderType.NORMAL
    }

    const product = resolveProductForSecurity(security, saved.product)
    const { price, triggerPrice } = resolveOpenPrice(
        mode,
        brokerOrderType,
        security,
        transactionType,
        explicitPrice,
        incoming.order?.triggerPrice
    )

    return {
        ...incoming,
        mode,
        type: incoming.type ?? 'trade',
        addStopLoss: false,
        addTarget: false,
        stopLoss: undefined,
        stopLossType: 'points',
        target: undefined,
        targetType: 'points',
        icebergLegs: mode === 'iceberg' ? incoming.icebergLegs ?? 1 : undefined,
        order: {
            ...incoming.order,
            orderId: undefined,
            orderExecutionParams: undefined,
            brokerOrderType,
            orderType,
            product,
            price,
            triggerPrice,
            validity: Validity.DAY,
            disclosedQuantity: 0,
            marketProtection: 0,
        },
    }
}

/** Exit / square-off opens: honour caller-supplied order details; only reset ancillary fields. */
function prepareExitQuickTradeOpenPayload(incoming: Partial<QuickTradeData>): Partial<QuickTradeData> {
    return {
        ...incoming,
        type: incoming.type ?? 'trade',
        addStopLoss: false,
        addTarget: false,
        stopLoss: undefined,
        stopLossType: 'points',
        target: undefined,
        targetType: 'points',
        icebergLegs: undefined,
        order: {
            ...incoming.order,
            triggerPrice: incoming.order?.triggerPrice ?? 0,
            validity: incoming.order?.validity ?? Validity.DAY,
            disclosedQuantity: incoming.order?.disclosedQuantity ?? 0,
            marketProtection: incoming.order?.marketProtection ?? 0,
        },
    }
}
